import {
  AdminTableContainer,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Banner,
  Button,
  buttonVariants,
  Checkbox,
  EmptyState,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  sonnerToast,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useDocumentTitle,
} from "@simplicity/ui";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { apiFetch } from "../../api";
import type {
  ApiKey,
  ArchiveConfig,
  Connector,
  Destination,
  ExportDefinition,
  Project,
  ProjectDetailResponse,
  ProjectStatus,
  TransformDefinition,
} from "../../configTypes";
import { useDetailForm } from "../../hooks/useDetailForm";
import { formatAbsoluteTime, formatRelativeTime } from "../../lib/formatRelativeTime";
import { DESTRUCTIVE_BUTTON_CLASS } from "../../utils";
import { TypeBadge } from "../TypeBadge";
import { ArchiveConfigEditor } from "./ArchiveConfigEditor";
import { Breadcrumbs } from "./Breadcrumbs";
import { LayerBadge } from "./LayerBadge";
import { ExportRunStatusBadge } from "./runStatus";
import { TransformLastRunCell } from "./TransformLastRunCell";
import { useDirtyForm } from "./useDirtyForm";
import { useTransformRunStatus } from "./useTransformRunStatus";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  active: "Active",
  paused: "Paused",
  offboarded: "Offboarded",
};

const STATUS_BADGE_VARIANT: Record<ProjectStatus, "warning" | "secondary"> = {
  active: "secondary",
  paused: "warning",
  offboarded: "secondary",
};

/**
 * Badge shown next to non-active projects so suppressed (paused/offboarded)
 * projects stay visually distinguished in lists. Renders nothing for active.
 */
export function ProjectStatusBadge({ status }: { status?: ProjectStatus }) {
  const resolved = status ?? "active";
  if (resolved === "active") return null;
  return <Badge variant={STATUS_BADGE_VARIANT[resolved]}>{STATUS_LABELS[resolved]}</Badge>;
}

function dateInputValue(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toISOString().slice(0, 10);
}

/**
 * SIM-1792: lifecycle-status fields, rendered inline in the project-edit form.
 * Controlled by the parent's `useDetailForm` state so status saves through the
 * single project Save — no separate mutation or save button.
 */
function StatusFields({
  status,
  setStatus,
  statusUntil,
  setStatusUntil,
  statusNote,
  setStatusNote,
  onChange,
}: {
  status: ProjectStatus;
  setStatus: (s: ProjectStatus) => void;
  statusUntil: string;
  setStatusUntil: (v: string) => void;
  statusNote: string;
  setStatusNote: (v: string) => void;
  onChange: () => void;
}) {
  return (
    <>
      <div className="mb-4">
        <Label htmlFor="project-status" className="form-label">
          Lifecycle status
        </Label>
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v as ProjectStatus);
            onChange();
          }}
        >
          <SelectTrigger id="project-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="offboarded">Offboarded</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {status === "paused" && (
        <div className="mb-4">
          <Label htmlFor="project-status-until" className="form-label">
            Return date (optional — leave blank for indefinite)
          </Label>
          <Input
            id="project-status-until"
            type="date"
            value={statusUntil}
            onChange={(e) => {
              setStatusUntil(e.target.value);
              onChange();
            }}
          />
        </div>
      )}
      {status !== "active" && (
        <div className="mb-4">
          <Label htmlFor="project-status-note" className="form-label">
            Status note
          </Label>
          <Textarea
            id="project-status-note"
            value={statusNote}
            onChange={(e) => {
              setStatusNote(e.target.value);
              onChange();
            }}
            placeholder="Optional context (e.g. reason for pause)"
          />
        </div>
      )}
    </>
  );
}

function ConnectorsTab({
  connectors,
  projectId,
  navigate,
}: {
  connectors: Connector[];
  projectId: string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <AdminTableContainer aria-label="Connectors">
      <div className="admin-table-header">
        <h2 className="text-lg text-foreground">Connectors</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void navigate(`/config/connectors/new?projectId=${projectId}`)}
        >
          Add
        </Button>
      </div>
      {connectors.length === 0 ? (
        <EmptyState title="No connectors" />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Prefix</th>
              <th>Created</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {connectors.map((c) => (
              <tr
                key={c.id}
                className="clickable"
                onClick={() => void navigate(`/config/connectors/${c.id}`)}
              >
                <td>{c.name}</td>
                <td>
                  <TypeBadge type={c.type} />
                </td>
                <td>{c.prefix}</td>
                <td className="col-muted">{formatDate(c.createdAt)}</td>
                <td className="col-muted">{formatDate(c.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </AdminTableContainer>
  );
}

function DestinationsTab({
  destinations,
  projectId,
  navigate,
}: {
  destinations: Destination[];
  projectId: string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <AdminTableContainer aria-label="Destinations">
      <div className="admin-table-header">
        <h2 className="text-lg text-foreground">Destinations</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void navigate(`/config/destinations/new?projectId=${projectId}`)}
        >
          Add
        </Button>
      </div>
      {destinations.length === 0 ? (
        <EmptyState title="No destinations" />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Created</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {destinations.map((d) => (
              <tr
                key={d.id}
                className="clickable"
                onClick={() => void navigate(`/config/destinations/${d.id}`)}
              >
                <td>{d.name}</td>
                <td>
                  <TypeBadge type={d.type} />
                </td>
                <td className="col-muted">{formatDate(d.createdAt)}</td>
                <td className="col-muted">{formatDate(d.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </AdminTableContainer>
  );
}

function TransformsTab({
  transformDefs,
  projectId,
  navigate,
}: {
  transformDefs: TransformDefinition[];
  projectId: string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  // SIM-2729 slice 2. Supplementary: when the endpoint is unavailable the column
  // renders em-dashes rather than "Never", because an absent row legitimately
  // means "never run" and reporting a failed fetch as never is the lying-UI
  // defect this ticket exists to remove.
  const runStatus = useTransformRunStatus(projectId);

  return (
    <AdminTableContainer aria-label="Transforms">
      <div className="admin-table-header">
        <h2 className="text-lg text-foreground">Transforms</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void navigate(`/config/transform-definitions/new?projectId=${projectId}`)}
        >
          Add
        </Button>
      </div>
      {transformDefs.length === 0 ? (
        <EmptyState title="No transforms" />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th className="w-10" />
              <th className="w-14">Order</th>
              <th>Name</th>
              <th>Layer</th>
              <th>Last run</th>
              <th>Updated</th>
              <th>Output Table</th>
            </tr>
          </thead>
          <tbody>
            {[...transformDefs]
              .sort((a, b) => a.order - b.order)
              .map((td) => (
                <tr
                  key={td.id}
                  className="clickable"
                  onClick={() => void navigate(`/config/transform-definitions/${td.id}`)}
                >
                  <td className="text-center" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={td.enabled} disabled />
                  </td>
                  <td className="text-center text-sm text-muted-foreground">{td.order}</td>
                  <td>{td.name}</td>
                  <td>
                    <LayerBadge layer={td.layer} />
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <TransformLastRunCell
                      run={
                        runStatus.status === "ready"
                          ? (runStatus.byDefinition.get(td.id) ?? null)
                          : null
                      }
                      loaded={runStatus.status !== "loading"}
                      nowMs={runStatus.computedAtMs}
                      lookbackDays={runStatus.lookbackDays}
                    />
                  </td>
                  <td className="col-muted">{formatDate(td.updatedAt)}</td>
                  {/* Last: it is the widest value and the least scanned. The
                      dependency count that used to sit here lives on the
                      definition page, which lists the dependencies themselves. */}
                  <td className="font-mono text-sm">
                    {td.outputDataset}.{td.outputTable}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </AdminTableContainer>
  );
}

function ExportsTab({
  exportDefs,
  projectId,
  navigate,
}: {
  exportDefs: ExportDefinition[];
  projectId: string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <AdminTableContainer aria-label="Exports">
      <div className="admin-table-header">
        <h2 className="text-lg text-foreground">Exports</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void navigate(`/config/export-definitions/new?projectId=${projectId}`)}
        >
          New export
        </Button>
      </div>
      {exportDefs.length === 0 ? (
        <EmptyState title="No export definitions" />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Cadence</th>
              <th>Format</th>
              <th>Destination</th>
              <th>Enabled</th>
              <th>Last Run</th>
            </tr>
          </thead>
          <tbody>
            {exportDefs.map((ed) => (
              <tr
                key={ed.id}
                className="clickable"
                onClick={() => void navigate(`/config/export-definitions/${ed.id}`)}
              >
                <td>{ed.name}</td>
                <td>
                  <TypeBadge type={ed.cadence} />
                </td>
                <td>{ed.format}</td>
                <td>
                  <TypeBadge type={ed.destinationType} />
                </td>
                <td>
                  <Badge variant={ed.enabled ? "success" : "secondary"}>
                    {ed.enabled ? "Yes" : "No"}
                  </Badge>
                </td>
                <td>
                  {ed.latestRun ? (
                    <ExportRunStatusBadge status={ed.latestRun.status} />
                  ) : (
                    <span className="text-sm italic text-muted-foreground">Never</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </AdminTableContainer>
  );
}

/**
 * SIM-3708: during a key rotation, the new key's "Last used" moving and the old key's going
 * stale is the evidence that the runner switched over and the old key is safe to revoke.
 */
function ApiKeyLastUsed({ lastUsedAt }: { lastUsedAt: string | null }) {
  if (lastUsedAt == null) {
    return <span className="italic text-muted-foreground">never</span>;
  }
  const epochMs = Date.parse(lastUsedAt);
  return (
    <time dateTime={lastUsedAt} title={formatAbsoluteTime(epochMs)}>
      {formatRelativeTime(epochMs)}
    </time>
  );
}

function ApiKeysTab({
  apiKeys,
  projectId,
  navigate,
  onRevoke,
}: {
  apiKeys: ApiKey[];
  projectId: string;
  navigate: ReturnType<typeof useNavigate>;
  onRevoke: (keyId: string) => void;
}) {
  return (
    <AdminTableContainer aria-label="API keys">
      <div className="admin-table-header">
        <h2 className="text-lg text-foreground">API Keys</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void navigate(`/config/api-keys/new?projectId=${projectId}`)}
        >
          Generate
        </Button>
      </div>
      {apiKeys.length === 0 ? (
        <EmptyState title="No API keys" />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Prefix</th>
              <th>Created</th>
              <th>Last used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {apiKeys.map((k) => (
              <tr key={k.id}>
                <td>{k.name}</td>
                <td className="col-muted font-mono">{k.kid}...</td>
                <td className="col-muted">{formatDate(k.createdAt)}</td>
                <td className="col-muted">
                  <ApiKeyLastUsed lastUsedAt={k.lastUsedAt} />
                </td>
                <td className="text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    className={DESTRUCTIVE_BUTTON_CLASS}
                    onClick={() => onRevoke(k.id)}
                  >
                    Revoke
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </AdminTableContainer>
  );
}

function ArchiveConfirmDialog({
  open,
  onOpenChange,
  projectName,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectName: string;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive project?</AlertDialogTitle>
          <AlertDialogDescription>
            This will archive &ldquo;{projectName}&rdquo; and all associated connectors,
            destinations, and API keys.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: "destructive" })}
            onClick={onConfirm}
          >
            Archive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RevokeKeyConfirmDialog({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revoke API key?</AlertDialogTitle>
          <AlertDialogDescription>
            This cannot be undone. Any runner using this key will stop authenticating.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: "destructive" })}
            onClick={onConfirm}
          >
            Revoke
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * SIM-1792: serialize the project-edit PATCH body. The pause window is only
 * sent when status is `paused` with a date; the server clears it otherwise.
 */
function buildProjectUpdateBody(fields: {
  name: string;
  status: ProjectStatus;
  statusUntil: string;
  statusNote: string;
}): string {
  const untilIso =
    fields.status === "paused" && fields.statusUntil
      ? new Date(`${fields.statusUntil}T00:00:00.000Z`).toISOString()
      : null;
  return JSON.stringify({
    name: fields.name.trim() || undefined,
    status: fields.status,
    statusUntil: untilIso,
    statusNote: fields.statusNote.trim() || null,
  });
}

function useProjectDetail(id: string | undefined) {
  const [project, setProject] = useState<Project | null>(null);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [transformDefs, setTransformDefs] = useState<TransformDefinition[]>([]);
  const [exportDefs, setExportDefs] = useState<ExportDefinition[]>([]);
  const [archiveConfig, setArchiveConfig] = useState<ArchiveConfig | null>(null);
  const [name, setName] = useState("");
  // SIM-1792: lifecycle status is part of the single project-edit form.
  const [status, setStatus] = useState<ProjectStatus>("active");
  const [statusUntil, setStatusUntil] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const { isDirty, setBaseline, resetBaseline } = useDirtyForm({
    name,
    status,
    statusUntil,
    statusNote,
  });

  const form = useDetailForm<ProjectDetailResponse, Project>({
    id,
    fetcher: (pid) => apiFetch<ProjectDetailResponse>(`/api/projects/${pid}/detail`),
    onLoaded: ({
      project: p,
      connectors: c,
      destinations: d,
      apiKeys: k,
      transformDefinitions: td,
      exportDefinitions: ed,
      archiveConfig: ac,
    }) => {
      setProject(p);
      setConnectors(c);
      setDestinations(d);
      setApiKeys(k);
      setTransformDefs(td);
      setExportDefs(ed);
      setArchiveConfig(ac);
      setName(p.name);
      const loadedStatus = p.status ?? "active";
      const loadedUntil = dateInputValue(p.statusUntil ?? null);
      const loadedNote = p.statusNote ?? "";
      setStatus(loadedStatus);
      setStatusUntil(loadedUntil);
      setStatusNote(loadedNote);
      setBaseline({
        name: p.name,
        status: loadedStatus,
        statusUntil: loadedUntil,
        statusNote: loadedNote,
      });
    },
    saver: () =>
      apiFetch<Project>(`/api/projects/${id}`, {
        method: "PATCH",
        body: buildProjectUpdateBody({ name, status, statusUntil, statusNote }),
      }),
    onSaved: (updated) => {
      setProject(updated);
      resetBaseline();
    },
    successMessage: "Project saved.",
  });

  return {
    project,
    setProject,
    connectors,
    destinations,
    apiKeys,
    setApiKeys,
    transformDefs,
    exportDefs,
    archiveConfig,
    setArchiveConfig,
    loading: form.loading,
    error: form.error,
    name,
    setName,
    status,
    setStatus,
    statusUntil,
    setStatusUntil,
    statusNote,
    setStatusNote,
    isDirty,
    saving: form.saving,
    save: form.save,
    notification: form.notification,
    saveLabel: form.saveLabel,
    clearNotification: form.clearNotification,
  };
}

function ProjectEditForm({
  project,
  detail,
  onArchiveClick,
}: {
  project: Project;
  detail: ReturnType<typeof useProjectDetail>;
  onArchiveClick: () => void;
}) {
  const {
    name,
    setName,
    status,
    setStatus,
    statusUntil,
    setStatusUntil,
    statusNote,
    setStatusNote,
    isDirty,
    saving,
    save,
    notification,
    saveLabel,
    clearNotification,
  } = detail;

  return (
    <div className="form-card mb-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg text-foreground">Edit Project</h2>
        <ProjectStatusBadge status={project.status} />
      </div>
      {notification && (
        <Banner variant={notification.variant} onDismiss={clearNotification} className="mb-4">
          {notification.message}
        </Banner>
      )}
      <div className="mb-4 font-mono text-sm text-muted-foreground">ID: {project.id}</div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="mb-4">
          <Label htmlFor="project-name" className="form-label">
            Name
          </Label>
          <Input
            id="project-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearNotification();
            }}
            placeholder="Project name"
            autoComplete="one-time-code"
          />
        </div>
        <StatusFields
          status={status}
          setStatus={setStatus}
          statusUntil={statusUntil}
          setStatusUntil={setStatusUntil}
          statusNote={statusNote}
          setStatusNote={setStatusNote}
          onChange={clearNotification}
        />
        <div className="form-actions">
          <Button
            type="button"
            variant="outline"
            className={DESTRUCTIVE_BUTTON_CLASS}
            onClick={onArchiveClick}
          >
            Archive
          </Button>
          <Button type="submit" disabled={saving || !isDirty || !name.trim()}>
            {saving ? "Saving..." : saveLabel}
          </Button>
        </div>
      </form>
    </div>
  );
}

async function archiveProject(projectId: string, navigate: ReturnType<typeof useNavigate>) {
  try {
    await apiFetch(`/api/projects/${projectId}/archive`, { method: "POST" });
    sonnerToast.info("Archived project");
    void navigate("/config/projects");
  } catch (err) {
    sonnerToast.error(err instanceof Error ? err.message : "Archive failed");
  }
}

async function revokeApiKey(
  projectId: string,
  keyId: string,
  setApiKeys: React.Dispatch<React.SetStateAction<ApiKey[]>>,
  setRevokeKeyId: React.Dispatch<React.SetStateAction<string | null>>,
) {
  try {
    await apiFetch(`/api/projects/${projectId}/api-keys/${keyId}`, { method: "DELETE" });
    setApiKeys((prev) => prev.filter((k) => k.id !== keyId));
    sonnerToast.info("API key revoked");
  } catch (err) {
    sonnerToast.error(err instanceof Error ? err.message : "Revoke failed");
  } finally {
    setRevokeKeyId(null);
  }
}

const TAB_VALUES = new Set([
  "connectors",
  "destinations",
  "transforms",
  "exports",
  "api-keys",
  "archive",
]);

function tabFromHash(hash: string): string {
  const value = hash.replace("#", "");
  return TAB_VALUES.has(value) ? value : "connectors";
}

export function ProjectDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => tabFromHash(location.hash));

  const detail = useProjectDetail(id);
  const { loading, error, project, isDirty, archiveConfig } = detail;

  const [revokeKeyId, setRevokeKeyId] = useState<string | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const { dialog: unsavedChangesDialog } = useUnsavedChangesGuard(isDirty);

  const archiveCount = archiveConfig?.enabled ? archiveConfig.mappings.length : 0;

  useDocumentTitle(project?.name ?? null, { suffix: " | Simplicity Admin" });

  if (loading) {
    return <div className="empty-state">Loading project...</div>;
  }

  if (error || !project || !id) {
    return <div className="empty-state text-destructive">{error ?? "Project not found"}</div>;
  }

  return (
    <div className="mx-auto max-w-4xl">
      <Breadcrumbs
        items={[{ label: "Projects", to: "/config/projects" }, { label: project.name }]}
      />

      <ProjectEditForm
        project={project}
        detail={detail}
        onArchiveClick={() => setArchiveOpen(true)}
      />

      {/* ─── Tabs ─────────────────────────────────────────────────────── */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v);
          window.history.replaceState(null, "", `#${v}`);
        }}
      >
        <TabsList className="mb-4">
          <TabsTrigger value="connectors">Connectors ({detail.connectors.length})</TabsTrigger>
          <TabsTrigger value="destinations">
            Destinations ({detail.destinations.length})
          </TabsTrigger>
          <TabsTrigger value="transforms">Transforms ({detail.transformDefs.length})</TabsTrigger>
          <TabsTrigger value="exports">Exports ({detail.exportDefs.length})</TabsTrigger>
          <TabsTrigger value="api-keys">API Keys ({detail.apiKeys.length})</TabsTrigger>
          <TabsTrigger value="archive">Archive ({archiveCount})</TabsTrigger>
        </TabsList>

        <TabsContent value="connectors">
          <ConnectorsTab connectors={detail.connectors} projectId={id} navigate={navigate} />
        </TabsContent>
        <TabsContent value="destinations">
          <DestinationsTab destinations={detail.destinations} projectId={id} navigate={navigate} />
        </TabsContent>
        <TabsContent value="transforms">
          <TransformsTab transformDefs={detail.transformDefs} projectId={id} navigate={navigate} />
        </TabsContent>
        <TabsContent value="exports">
          <ExportsTab exportDefs={detail.exportDefs} projectId={id} navigate={navigate} />
        </TabsContent>
        <TabsContent value="api-keys">
          <ApiKeysTab
            apiKeys={detail.apiKeys}
            projectId={id}
            navigate={navigate}
            onRevoke={setRevokeKeyId}
          />
        </TabsContent>
        <TabsContent value="archive">
          {id && (
            <ArchiveConfigEditor
              projectId={id}
              initialConfig={archiveConfig}
              onConfigChange={detail.setArchiveConfig}
            />
          )}
        </TabsContent>
      </Tabs>

      <ArchiveConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        projectName={project.name}
        onConfirm={() => void archiveProject(id, navigate)}
      />
      <RevokeKeyConfirmDialog
        open={!!revokeKeyId}
        onClose={() => setRevokeKeyId(null)}
        onConfirm={() =>
          revokeKeyId && void revokeApiKey(id, revokeKeyId, detail.setApiKeys, setRevokeKeyId)
        }
      />

      {unsavedChangesDialog}
    </div>
  );
}
